// apps/web/src/stores/auth-store.ts
import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';
import { setCookie, deleteCookie, getCookie } from '@/lib/cookies';
import { UserRole } from '@comp-sys/shared';

export interface AuthUser {
  id: string;
  phoneNumber: string | null;
  countryCode: string | null;
  email: string | null;
  displayName: string | null;
  role: UserRole;
  roles: UserRole[];
  activeRole: UserRole;
  schoolId: string | null;
  institutionId: string | null;
  hasCompletedOnboarding: boolean;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  rememberMe: boolean;
  accessTokenExpiresAt: number | null;
  setTokensWithRemember: (
    accessToken: string,
    refreshToken: string,
    rememberMe: boolean,
  ) => void;
  setImpersonatedSession: (
    accessToken: string,
    refreshToken: string,
    user: AuthUser,
  ) => void;
  setUser: (user: AuthUser | null) => void;
  switchRole: (role: UserRole, accessToken: string, refreshToken: string) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
  hydrateFromCookies: () => void;
}

/**
 * Decode JWT `exp` claim without verifying signature.
 * Returns milliseconds since epoch, or null on failure.
 */
function decodeJwtExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload.exp ? payload.exp * 1000 : null; // JWT exp is in seconds → ms
  } catch {
    return null;
  }
}

const STORAGE_KEY = 'auth-storage';

const createPersistentStorage = (): PersistStorage<Pick<AuthState, 'rememberMe'>> => ({
  getItem: (name: string) => {
    if (typeof window === 'undefined') return null;
    const value = localStorage.getItem(name);
    return value ? JSON.parse(value) : null;
  },
  setItem: (name: string, value: { state: { rememberMe: boolean }; version?: number }) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(name, JSON.stringify(value));
  },
  removeItem: (name: string) => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(name);
  },
});

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      rememberMe: false,
      accessTokenExpiresAt: null,
      setTokensWithRemember: (accessToken, refreshToken, rememberMe) => {
        const cookieDays = rememberMe ? 30 : 0;
        setCookie('accessToken', accessToken, cookieDays);
        setCookie('refreshToken', refreshToken, cookieDays);
        set({ accessToken, refreshToken, rememberMe, accessTokenExpiresAt: decodeJwtExpiry(accessToken) });
      },
      setImpersonatedSession: (accessToken, refreshToken, user) => {
        sessionStorage.setItem('impersonated_tokens', JSON.stringify({ accessToken, refreshToken }));
        set({ accessToken, refreshToken, user, accessTokenExpiresAt: decodeJwtExpiry(accessToken) });
      },
      setUser: (user) => set({ user }),
      switchRole: (role, accessToken, refreshToken) => {
        const cookieDays = get().rememberMe ? 30 : 0;
        setCookie('accessToken', accessToken, cookieDays);
        setCookie('refreshToken', refreshToken, cookieDays);
        set((state) => ({
          accessToken,
          refreshToken,
          accessTokenExpiresAt: decodeJwtExpiry(accessToken),
          user: state.user ? { ...state.user, activeRole: role } : state.user,
        }));
      },
      clear: () => {
        deleteCookie('accessToken');
        deleteCookie('refreshToken');
        sessionStorage.removeItem('impersonator');
        sessionStorage.removeItem('impersonated_tokens');
        useAuthStore.persist.clearStorage();
        set({ accessToken: null, refreshToken: null, user: null, rememberMe: false, accessTokenExpiresAt: null });
      },
      isAuthenticated: () => !!get().accessToken,
      hydrateFromCookies: () => {
        const stored = sessionStorage.getItem('impersonated_tokens');
        if (stored) {
          try {
            const { accessToken, refreshToken } = JSON.parse(stored);
            if (accessToken && refreshToken) {
              set({ accessToken, refreshToken, accessTokenExpiresAt: decodeJwtExpiry(accessToken) });
              return;
            }
          } catch { /* fall through to cookies */ }
        }
        const accessToken = getCookie('accessToken');
        const refreshToken = getCookie('refreshToken');
        if (accessToken && refreshToken) {
          set({ accessToken, refreshToken, accessTokenExpiresAt: decodeJwtExpiry(accessToken) });
        }
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createPersistentStorage(),
      partialize: (state) => ({
        rememberMe: state.rememberMe,
      }),
    },
  ),
);
